(function () {
  const variantCache = new Map();

  function localISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  function displayDate(date) {
    return `(${String(date.getDate()).padStart(2, '0')}.${String(
      date.getMonth() + 1
    ).padStart(2, '0')}.${date.getFullYear()})`;
  }

  function setPanel(root, name, open) {
    const panel = root.querySelector(`[data-tf-panel="${name}"]`);

    if (panel) {
      panel.hidden = !open;
    }
  }

  function getStoreRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function getStoreLocale() {
    const htmlLanguage = document.documentElement.lang || '';

    if (htmlLanguage.toLowerCase().startsWith('tr')) {
      return 'tr-TR';
    }

    return htmlLanguage || 'en-US';
  }

  function isTurkish() {
    return getStoreLocale().toLowerCase().startsWith('tr');
  }

  function formatMoney(priceInCents) {
    const price = Number(priceInCents || 0) / 100;
    const locale = getStoreLocale();
    const hasFraction = Math.round(price * 100) % 100 !== 0;

    try {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2
      }).format(price);

      return `${formatted} TL`;
    } catch (error) {
      return `${price.toLocaleString(locale)} TL`;
    }
  }

  async function getVariant(variantId) {
    const normalizedId = String(variantId || '').trim();

    if (!normalizedId) {
      return null;
    }

    if (variantCache.has(normalizedId)) {
      return variantCache.get(normalizedId);
    }

    const request = fetch(
      `${getStoreRoot()}variants/${encodeURIComponent(normalizedId)}.js`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(`Variant request failed: ${response.status}`);
        }

        return response.json();
      })
      .catch(function (error) {
        variantCache.delete(normalizedId);

        console.warn(`Varyant bilgisi alınamadı: ${normalizedId}`, error);

        return null;
      });

    variantCache.set(normalizedId, request);

    return request;
  }

  function getProductForm(root) {
    return (
      root.closest('form[action*="/cart/add"]') ||
      root.closest('form') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function getSubmitButton(root) {
    const form = getProductForm(root);

    return form
      ? form.querySelector('.btn-cart, button[type="submit"], input[type="submit"]')
      : null;
  }

  function getMainVariantId(root) {
    const form = getProductForm(root);

    if (!form) {
      return '';
    }

    const variantInput = form.querySelector(
      '[name="id"], input[name="id"], select[name="id"]'
    );

    return variantInput ? variantInput.value : '';
  }

  function isFieldVisible(field) {
    if (!field) {
      return false;
    }

    if (field.disabled) {
      return false;
    }

    if (field.closest('[hidden]')) {
      return false;
    }

    const style = window.getComputedStyle(field);

    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getRadioGroup(root, field) {
    if (!field.name) {
      return [field];
    }

    return Array.from(
      root.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(field.name)}"]`
      )
    ).filter(isFieldVisible);
  }

  function markFieldInvalid(field, invalid) {
    if (!field) {
      return;
    }

    field.setAttribute('aria-invalid', invalid ? 'true' : 'false');

    const fieldWrapper =
      field.closest('.tf-field') ||
      field.closest('.tf-choice-group') ||
      field.closest('.tf-date-choices') ||
      field.closest('.tf-options');

    if (fieldWrapper) {
      fieldWrapper.classList.toggle('tf-field-invalid', invalid);
    }
  }

  function clearValidation(root) {
    root.querySelectorAll('[aria-invalid="true"]').forEach(function (field) {
      markFieldInvalid(field, false);
    });

    const error = root.querySelector('[data-tf-error]');

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  function showValidationError(root, field, message) {
    const error = root.querySelector('[data-tf-error]');

    if (error) {
      error.textContent =
        message ||
        root.dataset.requiredMessage ||
        (isTurkish()
          ? 'Lütfen zorunlu alanları doldurun.'
          : 'Please complete the required fields.');

      error.hidden = false;
    }

    markFieldInvalid(field, true);

    const focusTarget =
      field?.type === 'radio'
        ? getRadioGroup(root, field)[0]
        : field;

    if (focusTarget) {
      focusTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      setTimeout(function () {
        focusTarget.focus({
          preventScroll: true
        });
      }, 300);
    }
  }

  function validateRequiredFields(root, showError) {
    clearValidation(root);

    const requiredFields = Array.from(root.querySelectorAll('[required]'));
    const processedRadioNames = new Set();

    for (const field of requiredFields) {
      if (!isFieldVisible(field)) {
        continue;
      }

      if (field.type === 'radio') {
        const radioName = field.name || field.id;

        if (processedRadioNames.has(radioName)) {
          continue;
        }

        processedRadioNames.add(radioName);

        const group = getRadioGroup(root, field);
        const selected = group.some(function (radio) {
          return radio.checked;
        });

        if (!selected) {
          if (showError) {
            showValidationError(root, group[0] || field);
          }

          return false;
        }

        continue;
      }

      if (field.type === 'checkbox') {
        if (!field.checked) {
          if (showError) {
            showValidationError(root, field);
          }

          return false;
        }

        continue;
      }

      const value = String(field.value || '').trim();

      if (!value) {
        if (showError) {
          showValidationError(root, field);
        }

        return false;
      }

      if (
        typeof field.checkValidity === 'function' &&
        !field.checkValidity()
      ) {
        if (showError) {
          showValidationError(
            root,
            field,
            field.validationMessage ||
              root.dataset.requiredMessage
          );
        }

        return false;
      }
    }

    const dateChoice = root.querySelector('[data-tf-date-choice]:checked');

    if (!dateChoice) {
      const firstDateChoice = root.querySelector('[data-tf-date-choice]');

      if (showError) {
        showValidationError(root, firstDateChoice);
      }

      return false;
    }

    if (dateChoice.value === 'custom') {
      const customDate = root.querySelector('[data-tf-delivery-date]');

      if (!customDate || !customDate.value) {
        if (showError) {
          showValidationError(root, customDate || dateChoice);
        }

        return false;
      }

      if (customDate.min && customDate.value < customDate.min) {
        if (showError) {
          showValidationError(
            root,
            customDate,
            isTurkish()
              ? 'Lütfen geçerli bir teslimat tarihi seçin.'
              : 'Please select a valid delivery date.'
          );
        }

        return false;
      }

      if (customDate.max && customDate.value > customDate.max) {
        if (showError) {
          showValidationError(
            root,
            customDate,
            isTurkish()
              ? 'Seçilen teslimat tarihi izin verilen tarih aralığının dışında.'
              : 'The selected delivery date is outside the allowed range.'
          );
        }

        return false;
      }
    }

    return true;
  }

  function syncSubmitButton(root) {
  const submitButton = getSubmitButton(root);

  if (!submitButton) {
    return;
  }

  const valid = validateRequiredFields(root, false);

  /*
   * Native disabled buton tıklama olayı üretmez.
   * Bu nedenle butonu gerçek anlamda disabled yapmıyoruz.
   * Görsel ve mantıksal pasifliği data attribute ile yönetiyoruz.
   */
  submitButton.disabled = false;
  submitButton.removeAttribute('disabled');

  submitButton.setAttribute(
    'aria-disabled',
    valid ? 'false' : 'true'
  );

  submitButton.classList.toggle(
    'tf-submit-disabled',
    !valid
  );

  if (valid) {
    submitButton.removeAttribute(
      'data-tf-required-disabled'
    );
  } else {
    submitButton.setAttribute(
      'data-tf-required-disabled',
      'true'
    );
  }
}

  function createTotalArea(root) {
    let totalArea = root.querySelector('[data-tf-order-total]');

    if (totalArea) {
      return totalArea;
    }

    totalArea = document.createElement('div');
    totalArea.className = 'tf-order-total';
    totalArea.setAttribute('data-tf-order-total', '');

    totalArea.innerHTML = `
      <span class="tf-order-total__label">
        ${
          isTurkish()
            ? 'Toplam Sipariş Tutarı:'
            : 'Total Order Amount:'
        }
      </span>

      <strong
        class="tf-order-total__value"
        data-tf-order-total-value
        aria-live="polite"
      >
        ${formatMoney(0)}
      </strong>
    `;

    const errorArea = root.querySelector('[data-tf-error]');

    if (errorArea) {
      root.insertBefore(totalArea, errorArea);
    } else {
      root.appendChild(totalArea);
    }

    return totalArea;
  }

  function injectStyles() {
    if (document.getElementById('tf-customization-runtime-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'tf-customization-runtime-styles';

    style.textContent = `
      .tf-order-total {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        width: 100%;
        margin-top: var(--tf-section-gap, 24px);
        padding: 18px 20px;
        border: var(--tf-border-width, 1px) solid var(--tf-border, #dddddd);
        border-radius: var(--tf-field-radius, 0);
        background: var(--tf-field-bg, #ffffff);
        color: var(--tf-text, #111111);
      }

      .tf-order-total__label {
        font-size: var(--tf-label-size, 16px);
        line-height: 1.35;
        font-weight: 500;
      }

      .tf-order-total__value {
        flex: 0 0 auto;
        font-size: calc(var(--tf-label-size, 16px) + 2px);
        line-height: 1.2;
        font-weight: 700;
        color: var(--tf-accent, #111111);
        text-align: right;
        white-space: nowrap;
      }

      .tf-order-total[data-loading="true"] .tf-order-total__value {
        opacity: 0.55;
      }

      [data-tf-customization] .tf-field-invalid input,
      [data-tf-customization] .tf-field-invalid select,
      [data-tf-customization] .tf-field-invalid textarea,
      [data-tf-customization] input[aria-invalid="true"],
      [data-tf-customization] select[aria-invalid="true"],
      [data-tf-customization] textarea[aria-invalid="true"] {
        border-color: var(--tf-error, #c62828) !important;
        box-shadow: 0 0 0 1px var(--tf-error, #c62828) !important;
      }

      [data-tf-customization] .tf-field-invalid .tf-choice-card {
        border-color: var(--tf-error, #c62828) !important;
      }

      .btn-cart.tf-submit-disabled,
      .btn-cart[data-tf-required-disabled="true"] {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
        pointer-events: auto !important;
      }

      @media screen and (max-width: 749px) {
        .tf-order-total {
          align-items: flex-start;
          padding: 15px 16px;
        }

        .tf-order-total__label {
          font-size: 14px;
        }

        .tf-order-total__value {
          font-size: 16px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  async function loadServiceVariantPrice(option) {
    const variantId = option.dataset.serviceVariantId;

    if (!variantId) {
      return null;
    }

    const regionName =
      option.dataset.regionName ||
      option.value ||
      option.textContent.trim();

    option.dataset.regionName = regionName;

    const variant = await getVariant(variantId);

    if (!variant || typeof variant.price === 'undefined') {
      return null;
    }

    option.dataset.variantPrice = String(variant.price);

    if (Number(variant.price) > 0) {
      option.textContent = `${regionName} (+${formatMoney(variant.price)})`;
    } else {
      option.textContent = regionName;
    }

    return variant;
  }

  async function initRegionPrices(root) {
    const regionOptions = root.querySelectorAll(
      'select[data-property-name*="Bölge"] option[data-service-variant-id], ' +
        'select[data-property-name*="Region"] option[data-service-variant-id]'
    );

    await Promise.all(
      Array.from(regionOptions).map(function (option) {
        return loadServiceVariantPrice(option);
      })
    );
  }

  async function getSelectedRegionPrice(root) {
    const selectedRegion = root.querySelector(
      'select[data-property-name*="Bölge"] option:checked,' +
        'select[data-property-name*="Region"] option:checked'
    );

    if (!selectedRegion) {
      return 0;
    }

    if (selectedRegion.dataset.variantPrice) {
      return Number(selectedRegion.dataset.variantPrice) || 0;
    }

    const serviceVariantId =
      selectedRegion.dataset.serviceVariantId || '';

    if (!serviceVariantId) {
      return 0;
    }

    const variant = await loadServiceVariantPrice(selectedRegion);

    return variant ? Number(variant.price) || 0 : 0;
  }

  async function getSelectedXsellTotal(root) {
    const selectedInputs = Array.from(
      root.querySelectorAll('[data-tf-xsell]:checked')
    );

    const prices = await Promise.all(
      selectedInputs.map(async function (input) {
        if (input.dataset.variantPrice) {
          return Number(input.dataset.variantPrice) || 0;
        }

        const variant = await getVariant(input.dataset.variantId);

        if (!variant) {
          return 0;
        }

        input.dataset.variantPrice = String(variant.price);

        return Number(variant.price) || 0;
      })
    );

    return prices.reduce(function (sum, price) {
      return sum + price;
    }, 0);
  }

  async function getMainProductPrice(root) {
    const variantId = getMainVariantId(root);

    if (!variantId) {
      return 0;
    }

    const variant = await getVariant(variantId);

    return variant ? Number(variant.price) || 0 : 0;
  }

  async function updateOrderTotal(root) {
    const totalArea = createTotalArea(root);
    const value = totalArea.querySelector('[data-tf-order-total-value]');

    if (!value) {
      return;
    }

    const calculationId = `${Date.now()}-${Math.random()}`;

    root.dataset.totalCalculationId = calculationId;
    totalArea.dataset.loading = 'true';

    const prices = await Promise.all([
      getMainProductPrice(root),
      getSelectedRegionPrice(root),
      getSelectedXsellTotal(root)
    ]);

    if (root.dataset.totalCalculationId !== calculationId) {
      return;
    }

    const total =
      Number(prices[0] || 0) +
      Number(prices[1] || 0) +
      Number(prices[2] || 0);

    value.textContent = formatMoney(total);
    totalArea.dataset.loading = 'false';
  }

  function scheduleUpdate(root) {
    window.clearTimeout(root.tfUpdateTimer);

    root.tfUpdateTimer = window.setTimeout(function () {
      syncSubmitButton(root);
      updateOrderTotal(root);
    }, 80);
  }

  function watchMainVariant(root) {
    const form = getProductForm(root);

    if (!form) {
      return;
    }

    let previousVariantId = getMainVariantId(root);

    root.tfVariantWatcher = window.setInterval(function () {
      if (!document.body.contains(root)) {
        window.clearInterval(root.tfVariantWatcher);
        return;
      }

      const currentVariantId = getMainVariantId(root);

      if (currentVariantId !== previousVariantId) {
        previousVariantId = currentVariantId;
        scheduleUpdate(root);
      }
    }, 400);
  }

  function init(root) {
    if (!root || root.dataset.tfInitialized === 'true') {
      return;
    }

    root.dataset.tfInitialized = 'true';

    injectStyles();
    createTotalArea(root);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayLabel = root.querySelector(
      '[data-tf-date-label="today"]'
    );

    const tomorrowLabel = root.querySelector(
      '[data-tf-date-label="tomorrow"]'
    );

    if (todayLabel) {
      todayLabel.textContent = displayDate(today);
    }

    if (tomorrowLabel) {
      tomorrowLabel.textContent = displayDate(tomorrow);
    }

    root.querySelectorAll('.tf-special-date').forEach(function (element) {
      const visibleFrom = element.dataset.visibleFrom
        ? new Date(`${element.dataset.visibleFrom}T00:00:00`)
        : null;

      const visibleUntil = element.dataset.visibleUntil
        ? new Date(`${element.dataset.visibleUntil}T23:59:59`)
        : null;

      element.hidden = Boolean(
        (visibleFrom && today < visibleFrom) ||
          (visibleUntil && today > visibleUntil)
      );
    });

    const customPanel = root.querySelector('[data-tf-custom-date]');
    const customInput = root.querySelector('[data-tf-delivery-date]');

    const customDateMaxDays = Math.max(
      0,
      parseInt(root.dataset.customDateMaxDays || '0', 10) || 0
    );

    if (customInput) {
      const minimumDate = new Date(today);
      const maximumDate = new Date(today);

      maximumDate.setDate(maximumDate.getDate() + customDateMaxDays);

      customInput.min = localISO(minimumDate);
      customInput.max = localISO(maximumDate);
    }

    root.querySelectorAll('[data-tf-date-choice]').forEach(function (input) {
      input.addEventListener('change', function () {
        const customSelected = input.value === 'custom';

        if (customPanel) {
          customPanel.hidden = !customSelected;
        }

        if (customInput) {
          customInput.required = customSelected;

          if (!customSelected) {
            customInput.value = '';
          }
        }

        scheduleUpdate(root);
      });
    });

    root
      .querySelectorAll('[data-tf-toggle-choice]')
      .forEach(function (input) {
        input.addEventListener('change', function () {
          setPanel(
            root,
            input.dataset.tfToggleChoice,
            input.value === 'yes'
          );

          scheduleUpdate(root);
        });
      });

    root.addEventListener('input', function () {
      scheduleUpdate(root);
    });

    root.addEventListener('change', function () {
      scheduleUpdate(root);
    });

    const form = getProductForm(root);

    if (form) {
      form.addEventListener(
        'submit',
        function (event) {
          if (!validateRequiredFields(root, true)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }

    initRegionPrices(root).then(function () {
      scheduleUpdate(root);
    });

    watchMainVariant(root);

    const buttonText = form && form.querySelector('.btn-cart span');

    if (buttonText && root.dataset.orderButtonLabel) {
      buttonText.textContent = root.dataset.orderButtonLabel;
    }

    scheduleUpdate(root);
  }

  function initAll() {
    document.querySelectorAll('[data-tf-customization]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

  window.TFProductCustomization = {
    validate(root, showError = true) {
      return validateRequiredFields(root, showError);
    },

    collect(root) {
      if (!validateRequiredFields(root, true)) {
        return null;
      }

      const properties = {};
      const selectedDateChoice = root.querySelector(
        '[data-tf-date-choice]:checked'
      );

      if (selectedDateChoice) {
        let date = '';
        let label = '';

        if (selectedDateChoice.value === 'today') {
          const selectedDate = new Date();

          selectedDate.setHours(0, 0, 0, 0);
          date = localISO(selectedDate);

          label =
            selectedDateChoice.nextElementSibling
              ?.querySelector('strong')
              ?.textContent.trim() ||
            selectedDateChoice.nextElementSibling?.textContent.trim() ||
            '';
        } else if (selectedDateChoice.value === 'tomorrow') {
          const selectedDate = new Date();

          selectedDate.setHours(0, 0, 0, 0);
          selectedDate.setDate(selectedDate.getDate() + 1);

          date = localISO(selectedDate);

          label =
            selectedDateChoice.nextElementSibling
              ?.querySelector('strong')
              ?.textContent.trim() ||
            selectedDateChoice.nextElementSibling?.textContent.trim() ||
            '';
        } else if (selectedDateChoice.value === 'custom') {
          const customDate = root.querySelector('[data-tf-delivery-date]');

          date = customDate ? customDate.value : '';
          label = date;
        } else {
          date = selectedDateChoice.dataset.specialDate || '';
          label = selectedDateChoice.dataset.specialLabel || '';
        }

        properties[
          isTurkish() ? 'Teslimat Tarihi' : 'Delivery Date'
        ] = `${label} (${date})`;
      }

      root.querySelectorAll('[data-tf-property]').forEach(function (field) {
        if (field.type === 'radio') {
          if (field.checked) {
            properties[field.dataset.propertyName] = field.value;
          }
        } else if (field.value) {
          properties[field.dataset.propertyName] = field.value;
        }
      });

      const phone = root.querySelector(
        'input[type="tel"][data-tf-property]'
      );

      if (phone && phone.value) {
        const phoneValue = phone.value.trim();

        properties[phone.dataset.propertyName] =
          phoneValue.startsWith('+')
            ? phoneValue
            : `+90 ${phoneValue}`;
      }

      const selectedRegion = root.querySelector(
        'select[data-property-name*="Bölge"] option:checked,' +
          'select[data-property-name*="Region"] option:checked'
      );

      const serviceVariantId =
        selectedRegion?.dataset.serviceVariantId || '';

      const xsells = Array.from(
        root.querySelectorAll('[data-tf-xsell]:checked')
      ).map(function (input) {
        return {
          id: Number(input.dataset.variantId),
          quantity: 1,
          properties: {
            [isTurkish() ? 'Ek ürün' : 'Add-on product']:
              input.dataset.productTitle
          }
        };
      });

      return {
        properties,
        xsells,
        serviceVariantId
      };
    }
  };

  (function () {
  'use strict';

  function moveCustomDateToEnd(root) {
    const scope = root || document;

    scope.querySelectorAll('[data-tf-date-options]').forEach(function (dateOptions) {
      const customDateInput = dateOptions.querySelector(
        [
          'input[data-tf-date-choice][value="custom"]',
          'input[data-tf-date-choice][value="other"]',
          'input[data-tf-date-choice][value="another"]'
        ].join(',')
      );

      let customDateCard = customDateInput
        ? customDateInput.closest('label, .tf-choice-card')
        : null;

      if (!customDateCard) {
        customDateCard = Array.from(
          dateOptions.querySelectorAll('label, .tf-choice-card')
        ).find(function (card) {
          return /başka\s+bir\s+tarih/i.test(card.textContent || '');
        });
      }

      if (customDateCard && dateOptions.lastElementChild !== customDateCard) {
        dateOptions.appendChild(customDateCard);
      }
    });
  }

  function init() {
    moveCustomDateToEnd(document);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          moveCustomDateToEnd(node);
          moveCustomDateToEnd(node.parentElement);
        });
      });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();
})();
})();